import { DataStorage } from '@studyportals/data-storage';
import type { IItemContext } from '@studyportals/datalayer';
import type {
	Action,
	ITrackingData
} from '@studyportals/datalake-event-tracker';
import { useRollbar } from "@composables/useRollbar";

type DataLakeModule = typeof import('@studyportals/datalake-event-tracker');
type TrackerInstance = InstanceType<DataLakeModule['Tracker']>;
type TrackingContext = {
	trackerModule: DataLakeModule;
	tracker: TrackerInstance;
};

type TrackingProvider = {
	trackSearch: (searchTerms: string[], filters: object, resultCounter: number) => void;
	trackImpression: (label: string, property: string) => void;
	trackClick: (label: string, property: string) => void;
	trackCardImpression: (cardId: number, organisationId: number, position: number) => void;
	trackCardClick: (cardId: number, organisationId: number, position: number) => void;
	trackUniversityCardClick: (organisationId: number, position: number) => void;
	trackUniversityCardImpression: (organisationId: number, position: number) => void;
	trackAction: (action: Action, label: string, property: string) => void;
	trackAPIResponseTime: (time: number) => void;
};

let dataLakeImportPromise: Promise<TrackingContext | null> | null = null;

const loadDataLake = async (): Promise<TrackingContext | null> => {
	if (!import.meta.client) return null;

	if (dataLakeImportPromise) return dataLakeImportPromise;

	dataLakeImportPromise = (async (): Promise<TrackingContext | null> => {
		try {
			const trackerModule = await import('@studyportals/datalake-event-tracker');

			return {
				trackerModule,
				tracker: new trackerModule.Tracker(trackerModule.Product.OMNI_SEARCH)
			};
		} catch {
			return null;
		}
	})();

	return dataLakeImportPromise;
};

const runWithDataLake = (callback: (trackingContext: TrackingContext) => void): void =>
	void loadDataLake().then((trackingContext) => {
		if (!trackingContext) return;

		try {
			callback(trackingContext);
		} catch (e) {
			const rollbar = useRollbar();
			if (!rollbar) return;

			rollbar.logError('Error in tracking callback', e);
		}
	});

const storeTrackingContext = (context: IItemContext, key: string): void => {
	const value = DataStorage.retrieve(key) as string;
	let data: IItemContext[] = [];
	if (value !== '') {
		data = data.concat(JSON.parse(value) as IItemContext[]);
	}

	data.push(context);

	DataStorage.store(key, JSON.stringify(data), 30);
};

export const useTracking = (): TrackingProvider => ({
	trackSearch: (searchTerms: string[], filters: object, resultCounter: number): void =>
		runWithDataLake(({ tracker }) => {
			const searchTermsArray = searchTerms.length ? searchTerms : [''];
			tracker.trackSiteSearch({
				terms: searchTermsArray,
				filters,
				totalResults: resultCounter,
			});
		}),

	trackImpression: (label: string, property: string): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const trackingData: ITrackingData = {
				action: trackerModule.Action.SEND,
				label,
				property: property?.toLowerCase(),
				category: trackerModule.Product.OMNI_SEARCH
			};

			tracker.trackStructuredEvent(trackingData);
		}),

	trackAction: (action: Action, label: string, property: string): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const trackingData: ITrackingData = {
				action,
				label,
				property: property?.toLowerCase(),
				category: trackerModule.Product.OMNI_SEARCH
			};

			tracker.trackStructuredEvent(trackingData);
		}),

	trackClick: (label: string, property: string): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const trackingData: ITrackingData = {
				action: trackerModule.Action.CLICK,
				label,
				property: property?.toLowerCase(),
			};

			tracker.trackStructuredEvent(trackingData);
		}),

	trackCardImpression: (cardId: number, organisationId: number, position: number): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const data = new trackerModule.StudyCardImpressionData(
				cardId,
				trackerModule.ListPageType.SEARCH,
				trackerModule.ListName.OMNI_SEARCH,
				organisationId,
				undefined,
				undefined,
				position,
				undefined,
				undefined
			);

			tracker.trackStudyCardImpression(data);
		}),

	trackCardClick: (cardId: number, organisationId: number, position: number): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const data = new trackerModule.ClickEcommerceTrackingData(
				cardId,
				trackerModule.ListPageType.SEARCH,
				trackerModule.ListName.OMNI_SEARCH,
				organisationId,
				undefined,
				undefined,
				position,
				undefined,
				undefined,
				trackerModule.ProductType.STUDY
			);

			storeTrackingContext(data.getTrackingContext(), 'StudyCardClick');
			tracker.trackStudyCardClick(data);
		}),

	trackUniversityCardClick: (organisationId: number, position: number): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const data = new trackerModule.UniversityCardClickEcommerceTrackingData(
				organisationId,
				trackerModule.ListPageType.SEARCH,
				trackerModule.ListName.OMNI_SEARCH,
				position,
			);

			storeTrackingContext(data.getTrackingContext(), 'UniversityCardClick');
			tracker.trackUniversityCardClick(data);
		}),
	trackUniversityCardImpression: (organisationId: number, position: number): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const data = new trackerModule.UniversityCardImpressionData(
				organisationId,
				trackerModule.ListPageType.SEARCH,
				trackerModule.ListName.OMNI_SEARCH,
				organisationId,
				position,
			);

			storeTrackingContext(data.getTrackingContext(), 'UniversityCardImpression');
			tracker.trackUniversityCardImpression(data);
		}),
	trackAPIResponseTime: (time: number): void =>
		runWithDataLake(({ trackerModule, tracker }) => {
			const trackingData: ITrackingData = {
				action: trackerModule.Action.TIME_SPENT,
				label: "search_api_response_time",
				property: 'programme_search',
				value: time
			};

			tracker.trackStructuredEvent(trackingData);
		}),
});
